import { useEffect, useMemo, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { fetchPendingAssignments } from 'api/dashboardAssignments'
import { classroomStore } from '../model/classroomStore'
import { ensureStudentsLoaded } from '../../Students/model/ensureStudentsLoaded'
import { studentsStore } from '../../Students/model/studentsStore'
import { ensureApplicationsLoaded } from '../../ApplicationsPage/model/ensureApplicationsLoaded'
import { ensureStudentProfileBuilderLoaded } from '../../../shared/stores/ensureStudentProfileBuilderLoaded'
import { ensureCurriculumLoaded } from '../../../shared/stores/ensureCurriculumLoaded'
import { curriculumStore } from '../../../shared/stores/curriculumStore'
import { useTasksPolling } from 'shared/hooks/useTasksPolling'

export default function useClassroomPage() {
	const location = useLocation()
	const openAssignmentId = location.state?.openAssignmentId
	const assignmentsLength = classroomStore.allAssignments?.length ?? 0
	const tasksLoadStatus = classroomStore.tasksLoadStatus
	const studentsCount = studentsStore.students?.length ?? 0
	const [activeTab, setActiveTab] = useState('all')
	const [extraItems, setExtraItems] = useState([])
	const [isCreateOpen, setIsCreateOpen] = useState(false)
	const [selectedAssignment, setSelectedAssignment] = useState(null)
	const [isDetailsOpen, setIsDetailsOpen] = useState(false)
	const [createModalPrefill, setCreateModalPrefill] = useState({
		assignmentType: '',
		title: '',
		grade: 'all',
	})
	const getGradeKeyFromStudentGrade = grade => {
		if (grade === undefined || grade === null) return null
		const str = String(grade).trim()
		if (!str) return null
		const match = str.match(/\d+/)
		if (!match) return null
		const n = parseInt(match[0], 10)
		if (!Number.isFinite(n) || n < 1 || n > 20) return null
		return String(n)
	}

	const setGrade = useCallback(id => {
		studentsStore.setGrade(id)
	}, [])

	const loadPendingNewState = useCallback(async () => {
		try {
			const res = await fetchPendingAssignments()
			const tasks = Array.isArray(res?.data?.tasks) ? res.data.tasks : []
			classroomStore.setPendingIsNewFromTasks(tasks)
		} catch (err) {
			console.error('Failed to load pending assignment new state:', err)
		}
	}, [])

	const loadTasks = useCallback(async () => {
		try {
			const tasks = await classroomStore.refreshTasks()
			classroomStore.reapplyPendingNewFlags()
			return tasks
		} catch (err) {
			console.error('Failed to load tasks:', err)
			return classroomStore.rawTasks || []
		}
	}, [])

	useTasksPolling({
		onUpdate: loadTasks,
		enabled: true,
		initialInterval: 30000,
		maxInterval: 300000,
		backoffMultiplier: 1.5,
	})

	useEffect(() => {
		loadPendingNewState()
	}, [loadPendingNewState])

	useEffect(() => {
		if (!classroomStore.tasksFetchedOnce) {
			loadTasks()
		}
		classroomStore.markAssignmentsSeen()
	}, [loadTasks])

	useEffect(() => {
		const filterIds = location.state?.filterIds
		if (!Array.isArray(filterIds) || filterIds.length === 0) return
		classroomStore.setActiveFilterIds(filterIds)
	}, [location.key, location.state])

	useEffect(() => {
		const state = location.state
		if (!state) return

		if (state.viewMode === 'curriculum') {
			curriculumStore.setViewMode('curriculum')
			if (!state.gradeTab) {
				const gradeId = curriculumStore.applyDefaultGradeForCurriculumView()
				const gradeTab = curriculumStore.classroomTabFromGradeId(gradeId)
				setActiveTab(gradeTab)
				curriculumStore.syncGradeFromClassroomTab(gradeTab)
			}
		} else if (state.viewMode === 'table') {
			curriculumStore.setViewMode('table')
		}

		if (state.gradeTab) {
			setActiveTab(state.gradeTab)
			curriculumStore.syncGradeFromClassroomTab(state.gradeTab)
			const gradeMatch = String(state.gradeTab).match(/^grade_(\d+)$/)
			if (gradeMatch) {
				studentsStore.setGrade(gradeMatch[1])
			}
		}

		if (state.assignmentTypeFilter) {
			classroomStore.setAssignmentTypeFilter(state.assignmentTypeFilter)
		}

		if (state.openCreateModal) {
			const { grade, assignmentType, title } = state.openCreateModal
			setCreateModalPrefill({
				grade: grade ?? 'all',
				assignmentType: assignmentType || '',
				title: title || '',
			})
			studentsStore.setGrade(grade ?? 'all')
			setIsCreateOpen(true)
		}
	}, [location.key, location.state])

	useEffect(() => {
		if (!openAssignmentId) return

		const assignments = classroomStore.allAssignments || []
		if (!assignments.length && tasksLoadStatus !== 'ready') {
			return
		}

		const assignment = assignments.find(
			item => String(item.id) === String(openAssignmentId),
		)
		if (!assignment) return

		setSelectedAssignment(assignment)
		setIsDetailsOpen(true)
	}, [openAssignmentId, assignmentsLength, tasksLoadStatus])

	// Load students using ensureStudentsLoaded to avoid race conditions
	useEffect(() => {
		const load = async () => {
			try {
				await ensureStudentsLoaded()
			} catch (e) {
				console.error(e)
			}
		}

		if (
			!studentsStore.students?.length ||
			(studentsStore.students || []).some(s => !s?.avatarKey)
		) {
			load()
		}
	}, [])

	useEffect(() => {
		ensureApplicationsLoaded().catch(err => {
			console.error('Failed to load applications:', err)
		})
	}, [])

	useEffect(() => {
		ensureStudentProfileBuilderLoaded().catch(err => {
			console.error('Failed to load profile builder data:', err)
		})
	}, [])

	useEffect(() => {
		ensureCurriculumLoaded().catch(err => {
			console.error('Failed to load curriculum:', err)
		})
	}, [])

	useEffect(() => {
		if (!studentsCount) return
		curriculumStore.syncDefaultGradeFromStudentsIfNeeded()
	}, [studentsCount])

	useEffect(() => {
		curriculumStore.bindClassroomActions({
			openCreateModal: ({ grade, assignmentType, title }) => {
				setCreateModalPrefill({
					grade: grade ?? 'all',
					assignmentType: assignmentType || '',
					title: title || '',
				})
				studentsStore.setGrade(grade ?? 'all')
				setIsCreateOpen(true)
			},
			openAssignmentDetails: assignment => {
				setSelectedAssignment(assignment)
				setIsDetailsOpen(true)
			},
			openAssignmentsTable: ({ grade, assignmentType }) => {
				const gradeTab =
					grade != null ? `grade_${grade}` : curriculumStore.classroomTabFromGradeId(
						curriculumStore.activeGradeId,
					)
				classroomStore.resetFiltersToAll()
				classroomStore.setAssignmentTypeFilter(assignmentType || '')
				setActiveTab(gradeTab)
				curriculumStore.syncGradeFromClassroomTab(gradeTab)
				curriculumStore.setViewMode('table')
			},
		})
	}, [])

	const { grade9Items, grade10Items, grade11Items, grade12Items, allItems } = useMemo(() => {
		const assignments = classroomStore?.allAssignments || []

		if (!assignments.length) {
			return {
				grade9Items: 0,
				grade10Items: 0,
				grade11Items: 0,
				grade12Items: 0,
				allItems: 0,
			}
		}

		let grade9Items = 0
		let grade10Items = 0
		let grade11Items = 0
		let grade12Items = 0

		assignments.forEach(item => {
			const students = item?.students || []
			const gradeKeys = new Set(
				students.map(s => getGradeKeyFromStudentGrade(s.grade)).filter(Boolean),
			)

			if (gradeKeys.has('9')) grade9Items++
			if (gradeKeys.has('10')) grade10Items++
			if (gradeKeys.has('11')) grade11Items++
			if (gradeKeys.has('12')) grade12Items++
		})

		return {
			grade9Items,
			grade10Items,
			grade11Items,
			grade12Items,
			allItems: assignments.length,
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [classroomStore?.allAssignments])

	useEffect(() => {
		if (!classroomStore?.allAssignments) return
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [classroomStore?.allAssignments])

	const tabs = useMemo(() => {
		return [
			{ id: 'all', label: 'All', count: allItems },
			{ id: 'grade_9', label: 'Grade 9', count: grade9Items },
			{ id: 'grade_10', label: 'Grade 10', count: grade10Items },
			{ id: 'grade_11', label: 'Grade 11', count: grade11Items },
			{ id: 'grade_12', label: 'Grade 12', count: grade12Items },
		]
	}, [allItems, grade9Items, grade10Items, grade11Items, grade12Items])

	const baseItems = useMemo(
		() => [
			{ id: 'all', label: 'All' },
			{ id: 'new', label: 'New' },
			{ id: 'active', label: 'Active' },
			{ id: 'completed', label: 'Completed' },
			{ id: 'low', label: 'Low Completion' },
		],
		[],
	)

	const actions = useMemo(() => {
		return [
			// {
			// 	id: 'settings',
			// 	icon: <SettingsIcon />,
			// 	onClick: () => {},
			// 	typeIconHover: 'stroke',
			// },
		]
	}, [])

	// Calculate KPI from actual assignments data (same as table)
	const kpiList = useMemo(() => {
		if (
			!classroomStore?.allAssignments ||
			classroomStore.allAssignments.length === 0
		) {
			return [
				{ id: 1, color: 'red', label: 'Active Assignments', value: '0' },
				{ id: 2, color: 'green', label: 'Total Completions', value: '0' },
				// { id: 3, color: 'yellow', label: 'Pending Tasks', value: '0' },
				{ id: 4, color: 'purple', label: 'Overall Completion', value: '0%' },
			]
		}

		const assignments = classroomStore.allAssignments

		const activeAssignments = assignments.filter(a => {
			const status = String(a.status || '')
				.toLowerCase()
				.trim()
			return (
				status === 'active' ||
				status === 'not_started' ||
				status === 'in_progress'
			)
		}).length

		// Count completed assignments
		const completedAssignments = assignments.filter(
			a =>
				String(a.status || '')
					.toLowerCase()
					.trim() === 'completed',
		).length

		// const pendingTasks = assignments.filter(a => {
		// 	const status = String(a.status || '')
		// 		.toLowerCase()
		// 		.trim()
		// 	const completion = Number(a.completion) || 0
		// 	return (
		// 		(status === 'active' ||
		// 			status === 'not_started' ||
		// 			status === 'in_progress') &&
		// 		completion < 60
		// 	)
		// }).length

		// Calculate overall completion percentage
		// Average completion percentage across all assignments
		let totalCompletion = 0
		let assignmentsWithCompletion = 0
		assignments.forEach(a => {
			const completion = Number(a.completion)
			if (!Number.isNaN(completion)) {
				totalCompletion += completion
				assignmentsWithCompletion++
			}
		})
		const overallCompletion =
			assignmentsWithCompletion > 0
				? Math.round(totalCompletion / assignmentsWithCompletion)
				: 0

		return [
			{
				id: 1,
				color: 'red',
				label: 'Active Assignments',
				value: String(activeAssignments),
			},
			{
				id: 2,
				color: 'green',
				label: 'Total Completions',
				value: String(completedAssignments),
			},
			// {
			// 	id: 3,
			// 	color: 'yellow',
			// 	label: 'Pending Tasks',
			// 	value: String(pendingTasks),
			// },
			{
				id: 4,
				color: 'purple',
				label: 'Overall Completion',
				value: `${overallCompletion}%`,
			},
		]
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [classroomStore?.allAssignments])

	const onSortChange = useCallback(opt => {
		if (!opt?.key || !opt?.dir) return
		classroomStore.setSort(opt.key, opt.dir)
	}, [])

	const sortKey = classroomStore.sortKey
	const sortDir = classroomStore.sortDir

	const allRows = classroomStore.filteredAssignments

	const rows = useMemo(() => {
		if (activeTab === 'all') return allRows

		const targetGradeKey = String(activeTab).startsWith('grade_')
			? String(activeTab).replace('grade_', '')
			: null

		if (!targetGradeKey) return allRows

		return allRows.filter(a => {
			const students = a?.students || []
			return students.some(
				s => getGradeKeyFromStudentGrade(s.grade) === targetGradeKey,
			)
		})
	}, [allRows, activeTab])

	const clearSort = useCallback(item => {
		setExtraItems(prev => prev.filter(x => x.id !== item.id))

		if (item.id === 'sort') {
			classroomStore.clearSort()
		}
	}, [])

	const handleTabSelect = useCallback(tabId => {
		setActiveTab(tabId)
		curriculumStore.syncGradeFromClassroomTab(tabId)
		classroomStore.clearAssignmentTypeFilter()
	}, [])

	const setViewMode = useCallback(mode => {
		if (mode === 'curriculum' && activeTab === 'all') {
			const gradeId = curriculumStore.applyDefaultGradeForCurriculumView()
			const gradeTab = curriculumStore.classroomTabFromGradeId(gradeId)
			setActiveTab(gradeTab)
			curriculumStore.syncGradeFromClassroomTab(gradeTab)
		}
		curriculumStore.setViewMode(mode)
	}, [activeTab])

	return {
		activeTab,
		setActiveTab,
		handleTabSelect,
		actions,
		tabs,
		setViewMode,
		kpiList,
		setGrade,
		rows,
		sortKey,
		sortDir,
		onSortChange,
		activeFilterIds: classroomStore.activeFilterIds,
		setActiveFilterIds: classroomStore.setActiveFilterIds,
		extraItems,
		setExtraItems,
		baseItems,
		clearSort,
		isCreateOpen,
		setIsCreateOpen,
		loadTasks,
		students: studentsStore.selectedStudents || studentsStore.students,
		selectedAssignment,
		setSelectedAssignment,
		isDetailsOpen,
		setIsDetailsOpen,
		tasksLoadStatus,
		loadPendingNewState,
		createModalPrefill,
		clearCreateModalPrefill: () =>
			setCreateModalPrefill({ assignmentType: '', title: '', grade: 'all' }),
	}
}
